/**
 * fidelizacion.ts — Handlers MSW para el módulo de Fidelización (puntos).
 *
 * QUÉ HACE:
 * - Simula API de fidelización con localStorage para modo demo
 * - CRUD de clientes, puntos, premios y configuración
 *
 * QUÉ EXPORTA: fidelizacionHandlers (array de handlers MSW)
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { rest } from "@/mocks/safeRest";
import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";

// ─── Keys de localStorage ─────────────────────────────────────────────────

const KEYS = {
  config: "fidelizacion_config",
  clientes: "fidelizacion_clientes",
  movimientos: "fidelizacion_movimientos",
  premios: "fidelizacion_premios",
};

// ─── Defaults ──────────────────────────────────────────────────────────────

const DEFAULT_CONFIG = {
  activo: true,
  modoAcumulacion: "por_monto",
  montoPorPunto: 10000,
  puntosOtorgados: 1,
  puntosPorArticulo: 1,
  valorPuntoCOP: 500,
  minimoParaCanjear: 10,
  mostrarEnFactura: true,
  nombrePrograma: "Puntos Altenot",
  acumularPuntosEnVentaCredito: false,
  recordatorioRegistroEnCaja: true,
  cadaNVentasSinCliente: 3,
  tiers: [
    {
      id: "tier_bronce",
      nombre: "Bronce",
      umbralPuntosHistoricos: 0,
      descuentoPct: 0,
    },
    {
      id: "tier_plata",
      nombre: "Plata",
      umbralPuntosHistoricos: 2000,
      descuentoPct: 3,
    },
    {
      id: "tier_oro",
      nombre: "Oro",
      umbralPuntosHistoricos: 8000,
      descuentoPct: 6,
    },
  ],
};

const DEFAULT_CLIENTES = [
  {
    id: "fid_owner",
    nombre: "Dueño (Consumo Interno)",
    cedula: "0000",
    telefono: "0000000000",
    direccion: "Local Principal",
    email: "dueño@altenot.com",
    codigoCliente: "OWN001",
    puntosActuales: 0,
    puntosHistoricos: 0,
    puntosRedimidos: 0,
    activo: true,
    creadoEn: "2024-01-01T00:00:00Z",
    ultimaCompra: new Date().toISOString(),
  },
  {
    id: "fid_c1",
    nombre: "María López",
    cedula: "1098765432",
    telefono: "3001234567",
    direccion: "Calle 45 #12-34",
    email: "maria@email.com",
    codigoCliente: "ML1098",
    puntosActuales: 45,
    puntosHistoricos: 120,
    puntosRedimidos: 75,
    activo: true,
    creadoEn: "2025-11-15T10:00:00Z",
    ultimaCompra: "2026-04-08T14:30:00Z",
  },
  {
    id: "fid_c2",
    nombre: "Carlos Ramírez",
    cedula: "1087654321",
    tipoDocumento: "CC",
    telefono: "3109876543",
    direccion: "Carrera 7 #22-10",
    email: "carlos@email.com",
    ciudad: "Bogotá, D.C.",
    departamento: "Bogotá",
    codigoCliente: "CR1087",
    puntosActuales: 230,
    puntosHistoricos: 350,
    puntosRedimidos: 120,
    activo: true,
    creadoEn: "2025-10-20T08:00:00Z",
    ultimaCompra: "2026-04-10T09:15:00Z",
  },
  {
    id: "fid_c3",
    nombre: "Ana Martínez",
    cedula: "1076543210",
    telefono: "3201112233",
    direccion: "Av. Boyacá #100-20",
    codigoCliente: "AM1076",
    puntosActuales: 12,
    puntosHistoricos: 12,
    puntosRedimidos: 0,
    activo: true,
    creadoEn: "2026-03-01T12:00:00Z",
    ultimaCompra: "2026-04-05T16:00:00Z",
  },
  {
    id: "fid_c4",
    nombre: "Pedro Sánchez",
    cedula: "1065432109",
    telefono: "3157778899",
    direccion: "Calle 80 #50-15",
    email: "pedro.s@email.com",
    codigoCliente: "PS1065",
    puntosActuales: 500,
    puntosHistoricos: 890,
    puntosRedimidos: 390,
    activo: true,
    creadoEn: "2025-08-10T09:30:00Z",
    ultimaCompra: "2026-04-11T11:00:00Z",
  },
  {
    id: "fid_c5",
    nombre: "Laura Gómez",
    cedula: "1054321098",
    telefono: "3224445566",
    codigoCliente: "LG1054",
    puntosActuales: 0,
    puntosHistoricos: 60,
    puntosRedimidos: 60,
    activo: false,
    creadoEn: "2025-12-01T14:00:00Z",
    ultimaCompra: "2026-01-20T10:00:00Z",
  },
  {
    id: "fid_c6",
    nombre: "Valentina Torres",
    cedula: "1043219876",
    telefono: "3112223344",
    direccion: "Calle 100 #15-22",
    email: "valen.torres@email.com",
    codigoCliente: "VT1043",
    puntosActuales: 875,
    puntosHistoricos: 1200,
    puntosRedimidos: 325,
    activo: true,
    creadoEn: "2025-07-14T08:00:00Z",
    ultimaCompra: "2026-04-12T10:30:00Z",
  },
  {
    id: "fid_c7",
    nombre: "Andrés Herrera",
    cedula: "1032108765",
    telefono: "3205556677",
    direccion: "Carrera 15 #30-45",
    codigoCliente: "AH1032",
    puntosActuales: 310,
    puntosHistoricos: 420,
    puntosRedimidos: 110,
    activo: true,
    creadoEn: "2025-09-03T11:00:00Z",
    ultimaCompra: "2026-04-07T15:45:00Z",
  },
  {
    id: "fid_c8",
    nombre: "Lucía Mendoza",
    cedula: "1021097654",
    telefono: "3147778899",
    direccion: "Av. Las Américas #200-10",
    email: "lucia.m@email.com",
    codigoCliente: "LM1021",
    puntosActuales: 155,
    puntosHistoricos: 290,
    puntosRedimidos: 135,
    activo: true,
    creadoEn: "2025-11-22T09:00:00Z",
    ultimaCompra: "2026-04-09T12:00:00Z",
  },
  {
    id: "fid_c9",
    nombre: "Sebastián Castillo",
    cedula: "1010986543",
    telefono: "3016667788",
    direccion: "Calle 50 #8-90",
    codigoCliente: "SC1010",
    puntosActuales: 620,
    puntosHistoricos: 800,
    puntosRedimidos: 180,
    activo: true,
    creadoEn: "2025-06-18T14:00:00Z",
    ultimaCompra: "2026-04-11T08:30:00Z",
  },
  {
    id: "fid_c10",
    nombre: "Isabella Ríos",
    cedula: "1009875432",
    telefono: "3189990011",
    direccion: "Carrera 30 #60-15",
    email: "isa.rios@email.com",
    codigoCliente: "IR1009",
    puntosActuales: 88,
    puntosHistoricos: 150,
    puntosRedimidos: 62,
    activo: true,
    creadoEn: "2026-01-10T10:00:00Z",
    ultimaCompra: "2026-04-03T17:00:00Z",
  },
  {
    id: "fid_c11",
    nombre: "Felipe Morales",
    cedula: "1098764321",
    telefono: "3002223344",
    direccion: "Calle 23 #40-12",
    codigoCliente: "FM1098",
    puntosActuales: 1200,
    puntosHistoricos: 1800,
    puntosRedimidos: 600,
    activo: true,
    creadoEn: "2025-05-01T08:00:00Z",
    ultimaCompra: "2026-04-12T09:00:00Z",
  },
  {
    id: "fid_c12",
    nombre: "Camila Vargas",
    cedula: "1087653210",
    telefono: "3118884455",
    direccion: "Av. El Dorado #55-30",
    email: "camila.v@email.com",
    codigoCliente: "CV1087",
    puntosActuales: 450,
    puntosHistoricos: 600,
    puntosRedimidos: 150,
    activo: true,
    creadoEn: "2025-10-05T13:00:00Z",
    ultimaCompra: "2026-04-10T16:30:00Z",
  },
  {
    id: "fid_c13",
    nombre: "Diego Romero",
    cedula: "1076542109",
    telefono: "3204445566",
    direccion: "Carrera 50 #12-80",
    codigoCliente: "DR1076",
    puntosActuales: 35,
    puntosHistoricos: 35,
    puntosRedimidos: 0,
    activo: true,
    creadoEn: "2026-03-20T11:00:00Z",
    ultimaCompra: "2026-04-01T14:00:00Z",
  },
  {
    id: "fid_c14",
    nombre: "Mariana Castro",
    cedula: "1065431098",
    telefono: "3156667788",
    direccion: "Calle 72 #25-60",
    email: "mari.castro@email.com",
    codigoCliente: "MC1065",
    puntosActuales: 720,
    puntosHistoricos: 980,
    puntosRedimidos: 260,
    activo: true,
    creadoEn: "2025-08-28T09:30:00Z",
    ultimaCompra: "2026-04-11T13:45:00Z",
  },
  {
    id: "fid_c15",
    nombre: "Tomás Peña",
    cedula: "1054320987",
    telefono: "3223334455",
    direccion: "Calle 10 #5-18",
    codigoCliente: "TP1054",
    puntosActuales: 0,
    puntosHistoricos: 25,
    puntosRedimidos: 25,
    activo: false,
    creadoEn: "2026-02-14T10:00:00Z",
  },
];

const DEFAULT_MOVIMIENTOS = [
  {
    id: "fid_m1",
    clienteId: "fid_c2",
    clienteNombre: "Carlos Ramírez",
    tipo: "acumulacion",
    puntos: 15,
    saldoResultante: 230,
    descripcion: "Compra #V-042 - $150,000",
    ventaId: "v_demo_042",
    montoCompra: 150000,
    usuarioId: "usr_1",
    userName: "Juan Pérez",
    fecha: "2026-04-10T09:15:00Z",
  },
  {
    id: "fid_m2",
    clienteId: "fid_c4",
    clienteNombre: "Pedro Sánchez",
    tipo: "redencion",
    puntos: -50,
    saldoResultante: 500,
    descripcion: "Canje de descuento en compra",
    ventaId: "v_demo_038",
    usuarioId: "usr_1",
    userName: "Juan Pérez",
    fecha: "2026-04-09T15:00:00Z",
  },
  {
    id: "fid_m3",
    clienteId: "fid_c1",
    clienteNombre: "María López",
    tipo: "acumulacion",
    puntos: 8,
    saldoResultante: 45,
    descripcion: "Compra #V-040 - $80,000",
    ventaId: "v_demo_040",
    montoCompra: 80000,
    usuarioId: "usr_1",
    userName: "Juan Pérez",
    fecha: "2026-04-08T14:30:00Z",
  },
];

const DEFAULT_PREMIOS = [
  {
    id: "premio_1",
    nombre: "Descuento 10%",
    descripcion: "10% de descuento en tu próxima compra",
    puntosRequeridos: 50,
    stock: 99,
    activo: true,
  },
  {
    id: "premio_2",
    nombre: "Producto gratis",
    descripcion: "Un producto de hasta $15,000 gratis",
    puntosRequeridos: 200,
    stock: 10,
    activo: true,
  },
  {
    id: "premio_3",
    nombre: "Bolsa de compras premium",
    descripcion: "Bolsa ecológica con logo del negocio",
    puntosRequeridos: 30,
    stock: 25,
    activo: true,
  },
  {
    id: "premio_4",
    nombre: "Descuento 20%",
    descripcion: "20% de descuento en toda la tienda",
    puntosRequeridos: 120,
    stock: 50,
    activo: true,
  },
  {
    id: "premio_5",
    nombre: "Envío gratis",
    descripcion: "Envío a domicilio sin costo en tu próximo pedido",
    puntosRequeridos: 40,
    stock: 200,
    activo: true,
  },
  {
    id: "premio_6",
    nombre: "Bebida de cortesía",
    descripcion: "Una bebida gratis a elección del cliente",
    puntosRequeridos: 25,
    stock: 60,
    activo: true,
  },
  {
    id: "premio_7",
    nombre: "Postre gratis",
    descripcion: "Un postre de la vitrina sin costo",
    puntosRequeridos: 35,
    stock: 40,
    activo: true,
  },
  {
    id: "premio_8",
    nombre: "Membresía VIP 1 mes",
    descripcion: "Acceso a precios especiales por 30 días",
    puntosRequeridos: 500,
    stock: 5,
    activo: true,
  },
  {
    id: "premio_9",
    nombre: "Descuento 5%",
    descripcion: "5% de descuento en tu próxima compra",
    puntosRequeridos: 20,
    stock: 999,
    activo: true,
  },
  {
    id: "premio_10",
    nombre: "Kit de regalo",
    descripcion: "Caja sorpresa con productos seleccionados",
    puntosRequeridos: 350,
    stock: 8,
    activo: true,
  },
  {
    id: "premio_11",
    nombre: "Cupón $10,000",
    descripcion: "Vale de $10,000 pesos para tu próxima compra",
    puntosRequeridos: 80,
    stock: 30,
    activo: true,
  },
  {
    id: "premio_12",
    nombre: "Cupón $25,000",
    descripcion: "Vale de $25,000 pesos para canjear en tienda",
    puntosRequeridos: 180,
    stock: 15,
    activo: true,
  },
  {
    id: "premio_13",
    nombre: "Agenda personalizada",
    descripcion: "Agenda con logo del negocio y nombre del cliente",
    puntosRequeridos: 90,
    stock: 20,
    activo: false,
  },
  {
    id: "premio_14",
    nombre: "Termo reutilizable",
    descripcion: "Termo de acero inoxidable de 500ml",
    puntosRequeridos: 280,
    stock: 12,
    activo: true,
  },
  {
    id: "premio_15",
    nombre: "Accesorio sorpresa",
    descripcion: "Un accesorio especial escogido por el negocio",
    puntosRequeridos: 150,
    stock: 18,
    activo: true,
  },
];

export const FIDELIZACION_DEFAULT_CONFIG = DEFAULT_CONFIG;
export const FIDELIZACION_DEFAULT_CLIENTES = DEFAULT_CLIENTES;
export const FIDELIZACION_DEFAULT_MOVIMIENTOS = DEFAULT_MOVIMIENTOS;
export const FIDELIZACION_DEFAULT_PREMIOS = DEFAULT_PREMIOS;

// ─── Helpers ──────────────────────────────────────────────────────────────

const isRealUser = (req: any): boolean => {
  const auth = req.headers.get("authorization") || "";
  const token = auth.replace(/^Bearer\s+/i, "").trim();
  return token.length > 20 && !token.startsWith("mock-token-");
};

const genId = (prefix: string) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const genCodigoCliente = (nombre: string, cedula: string): string => {
  const iniciales = nombre
    .split(" ")
    .map((p) => p[0]?.toUpperCase() || "")
    .join("")
    .slice(0, 2);
  return `${iniciales}${cedula.slice(-4)}`;
};

// ─── Handlers ─────────────────────────────────────────────────────────────

export const fidelizacionHandlers = [
  // ── Config ──────────────────────────────────────────────────────────

  rest.get(/\/api\/fidelizacion\/config/, (req: any, res: any, ctx: any) => {
    if (isRealUser(req)) return;
    const config = txRead(KEYS.config, DEFAULT_CONFIG);
    return res(ctx.status(200), ctx.json({ success: true, data: config }));
  }),

  rest.put(
    /\/api\/fidelizacion\/config/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const current = txRead(KEYS.config, DEFAULT_CONFIG);
      const updated = { ...current, ...body };
      txWrite(KEYS.config, updated);
      return res(ctx.status(200), ctx.json({ success: true, data: updated }));
    },
  ),

  // ── Clientes ────────────────────────────────────────────────────────

  rest.get(
    /\/api\/fidelizacion\/clientes\/buscar/,
    (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const url = new URL(req.url);
      const q = (url.searchParams.get("q") || "").toLowerCase().trim();
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);

      // Búsqueda flexible: por cédula, código, teléfono o nombre (parcial y multi-palabra)
      const searchTerms = q.split(/\s+/).filter(Boolean);
      const found = clientes.find((c: any) => {
        if (!q) return false;

        // Match exacto en campos clave
        if (
          c.cedula.toLowerCase() === q ||
          c.codigoCliente.toLowerCase() === q ||
          c.telefono === q
        )
          return true;

        // Match parcial flexible en nombre y otros campos
        const target =
          `${c.nombre} ${c.cedula} ${c.codigoCliente}`.toLowerCase();
        return searchTerms.every((term) => target.includes(term));
      });

      return res(
        ctx.status(200),
        ctx.json({ success: true, data: found || null }),
      );
    },
  ),

  rest.get(/\/api\/fidelizacion\/clientes/, (req: any, res: any, ctx: any) => {
    if (isRealUser(req)) return;
    const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);

    // Asegurar que el dueño siempre esté para pruebas rápidas
    const hasOwner = clientes.some(
      (c: any) =>
        c.id === "fid_owner" || c.nombre.toLowerCase().includes("dueño"),
    );
    if (!hasOwner) {
      clientes.unshift(DEFAULT_CLIENTES[0]);
      txWrite(KEYS.clientes, clientes);
    }

    return res(
      ctx.status(200),
      ctx.json({ success: true, data: { clientes } }),
    );
  }),

  rest.post(
    /\/api\/fidelizacion\/clientes/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);

      // Validar cédula duplicada
      if (clientes.some((c: any) => c.cedula === body.cedula)) {
        return res(
          ctx.status(400),
          ctx.json({
            success: false,
            message: "Ya existe un cliente con esa cédula",
          }),
        );
      }

      const nuevo: any = {
        id: genId("fid_c"),
        nombre: body.nombre,
        cedula: body.cedula,
        tipoDocumento: body.tipoDocumento || "CC",
        telefono: body.telefono,
        direccion: body.direccion || "",
        email: body.email || "",
        ciudad: body.ciudad || "",
        departamento: body.departamento || "",
        codigoCliente: genCodigoCliente(body.nombre, body.cedula),
        puntosActuales: 0,
        puntosHistoricos: 0,
        puntosRedimidos: 0,
        activo: true,
        creadoEn: new Date().toISOString(),
      };
      clientes.unshift(nuevo);
      txWrite(KEYS.clientes, clientes);
      return res(ctx.status(201), ctx.json({ success: true, data: nuevo }));
    },
  ),

  rest.put(
    /\/api\/fidelizacion\/clientes\/(.+)/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const id = req.params[0];
      const body = await req.json();
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      const idx = clientes.findIndex((c: any) => c.id === id);
      if (idx === -1) {
        return res(
          ctx.status(404),
          ctx.json({ success: false, message: "Cliente no encontrado" }),
        );
      }
      // Solo actualizar campos permitidos (no puntos desde aquí)
      const allowed = [
        "nombre",
        "telefono",
        "direccion",
        "email",
        "activo",
        "tipoDocumento",
        "ciudad",
        "departamento",
        "cedula",
      ];
      allowed.forEach((key) => {
        if (body[key] !== undefined) (clientes[idx] as any)[key] = body[key];
      });
      txWrite(KEYS.clientes, clientes);
      return res(
        ctx.status(200),
        ctx.json({ success: true, data: clientes[idx] }),
      );
    },
  ),

  rest.delete(
    /\/api\/fidelizacion\/clientes\/(.+)/,
    (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const id = req.params[0];
      let clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      clientes = clientes.filter((c: any) => c.id !== id);
      txWrite(KEYS.clientes, clientes);
      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  // ── Reiniciar puntos de un cliente ──────────────────────────────────

  rest.post(
    /\/api\/fidelizacion\/clientes\/(.+)\/reiniciar/,
    (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const id = req.params[0];
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      const idx = clientes.findIndex((c: any) => c.id === id);
      if (idx === -1) {
        return res(ctx.status(404), ctx.json({ success: false }));
      }
      runTransaction(() => {
        clientes[idx].puntosActuales = 0;
        txWrite(KEYS.clientes, clientes);

        // Registrar movimiento
        const movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
        movimientos.unshift({
          id: genId("fid_m"),
          clienteId: id,
          clienteNombre: clientes[idx].nombre,
          tipo: "reinicio",
          puntos: -clientes[idx].puntosActuales,
          saldoResultante: 0,
          descripcion: "Reinicio de puntos por administrador",
          usuarioId: "usr_1",
          userName: "Admin",
          fecha: new Date().toISOString(),
        } as any);
        txWrite(KEYS.movimientos, movimientos);
      });

      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  // ── Puntos — Acumular ──────────────────────────────────────────────

  rest.post(
    /\/api\/fidelizacion\/puntos\/acumular/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const config = txRead(KEYS.config, DEFAULT_CONFIG);
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      const idx = clientes.findIndex((c: any) => c.id === body.clienteId);

      if (idx === -1) {
        return res(
          ctx.status(404),
          ctx.json({ success: false, message: "Cliente no encontrado" }),
        );
      }

      // Calcular puntos según modo
      let puntosOtorgados = 0;
      if (config.modoAcumulacion === "por_monto") {
        puntosOtorgados =
          Math.floor(body.montoCompra / config.montoPorPunto) *
          config.puntosOtorgados;
      } else {
        puntosOtorgados =
          (body.cantidadArticulos || 0) * config.puntosPorArticulo;
      }

      if (puntosOtorgados <= 0) {
        return res(
          ctx.status(200),
          ctx.json({
            success: true,
            data: {
              puntosOtorgados: 0,
              nuevoSaldo: clientes[idx].puntosActuales,
            },
          }),
        );
      }

      runTransaction(() => {
        clientes[idx].puntosActuales += puntosOtorgados;
        clientes[idx].puntosHistoricos += puntosOtorgados;
        clientes[idx].ultimaCompra = new Date().toISOString();
        txWrite(KEYS.clientes, clientes);

        // Registrar movimiento
        const movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
        movimientos.unshift({
          id: genId("fid_m"),
          clienteId: body.clienteId,
          clienteNombre: clientes[idx].nombre,
          tipo: "acumulacion",
          puntos: puntosOtorgados,
          saldoResultante: clientes[idx].puntosActuales,
          descripcion: `Compra - $${body.montoCompra?.toLocaleString("es-CO") || 0}`,
          ventaId: body.ventaId,
          montoCompra: body.montoCompra,
          productos: body.productos || [],
          usuarioId: "usr_1",
          userName: "Cajero",
          fecha: new Date().toISOString(),
        } as any);
        txWrite(KEYS.movimientos, movimientos);
      });

      return res(
        ctx.status(200),
        ctx.json({
          success: true,
          data: {
            puntosOtorgados,
            nuevoSaldo: clientes[idx].puntosActuales,
          },
        }),
      );
    },
  ),

  // ── Puntos — Redimir ───────────────────────────────────────────────

  rest.post(
    /\/api\/fidelizacion\/puntos\/redimir/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      const config = txRead(KEYS.config, DEFAULT_CONFIG);
      const idx = clientes.findIndex((c: any) => c.id === body.clienteId);

      if (idx === -1) {
        return res(
          ctx.status(404),
          ctx.json({ success: false, message: "Cliente no encontrado" }),
        );
      }

      if (body.puntos > clientes[idx].puntosActuales) {
        return res(
          ctx.status(400),
          ctx.json({ success: false, message: "Puntos insuficientes" }),
        );
      }

      if (body.puntos < config.minimoParaCanjear) {
        return res(
          ctx.status(400),
          ctx.json({
            success: false,
            message: `Mínimo ${config.minimoParaCanjear} puntos para canjear`,
          }),
        );
      }

      runTransaction(() => {
        clientes[idx].puntosActuales -= body.puntos;
        clientes[idx].puntosRedimidos += body.puntos;
        txWrite(KEYS.clientes, clientes);

        // Descontar stock del premio si se especificó y registrar movimiento
        if (body.premioId) {
          const premios = txRead(KEYS.premios, DEFAULT_PREMIOS);
          const pidx = premios.findIndex((p: any) => p.id === body.premioId);
          if (pidx !== -1 && premios[pidx].stock > 0) {
            const stockAntes = premios[pidx].stock;
            premios[pidx].stock -= 1;
            const stockDespues = premios[pidx].stock;
            txWrite(KEYS.premios, premios);
            // Registrar movimiento de stock por canje
            const movimientosStock = txRead(
              KEYS.movimientos,
              DEFAULT_MOVIMIENTOS,
            );
            movimientosStock.unshift({
              id: genId("fid_m"),
              clienteId: body.clienteId,
              clienteNombre: clientes[idx].nombre,
              clienteCedula: clientes[idx].cedula,
              tipo: "ajuste_stock_premio",
              puntos: 0,
              saldoResultante: 0,
              descripcion: `Stock descontado por canje de ${clientes[idx].nombre}`,
              premioId: body.premioId,
              premioNombre: body.premioNombre,
              stockAntes,
              stockDespues,
              usuarioId: "usr_1",
              userName: "Cajero",
              fecha: new Date().toISOString(),
            } as any);
            txWrite(KEYS.movimientos, movimientosStock);
          }
        }

        const movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
        movimientos.unshift({
          id: genId("fid_m"),
          clienteId: body.clienteId,
          clienteNombre: clientes[idx].nombre,
          clienteCedula: clientes[idx].cedula,
          tipo: "redencion",
          puntos: -body.puntos,
          saldoResultante: clientes[idx].puntosActuales,
          descripcion: body.descripcion || "Redención de puntos",
          ventaId: body.ventaId,
          premioId: body.premioId,
          premioNombre: body.premioNombre,
          usuarioId: "usr_1",
          userName: "Cajero",
          fecha: new Date().toISOString(),
        } as any);
        txWrite(KEYS.movimientos, movimientos);
      });

      return res(
        ctx.status(200),
        ctx.json({
          success: true,
          data: { nuevoSaldo: clientes[idx].puntosActuales },
        }),
      );
    },
  ),

  // ── Puntos — Ajuste manual ─────────────────────────────────────────

  rest.post(
    /\/api\/fidelizacion\/puntos\/ajustar/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      const idx = clientes.findIndex((c: any) => c.id === body.clienteId);

      if (idx === -1) {
        return res(ctx.status(404), ctx.json({ success: false }));
      }

      runTransaction(() => {
        clientes[idx].puntosActuales += body.puntos;
        if (clientes[idx].puntosActuales < 0) clientes[idx].puntosActuales = 0;
        if (body.puntos > 0) clientes[idx].puntosHistoricos += body.puntos;
        txWrite(KEYS.clientes, clientes);

        const movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
        movimientos.unshift({
          id: genId("fid_m"),
          clienteId: body.clienteId,
          clienteNombre: clientes[idx].nombre,
          tipo: "ajuste_manual",
          puntos: body.puntos,
          saldoResultante: clientes[idx].puntosActuales,
          descripcion: body.descripcion || "Ajuste manual de puntos",
          usuarioId: body.usuarioId,
          userName: body.userName,
          fecha: new Date().toISOString(),
        } as any);
        txWrite(KEYS.movimientos, movimientos);
      });

      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  // ── Reiniciar TODOS los puntos ─────────────────────────────────────

  rest.post(
    /\/api\/fidelizacion\/puntos\/reiniciar-todos/,
    (_req: any, res: any, ctx: any) => {
      const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
      clientes.forEach((c: any) => {
        c.puntosActuales = 0;
      });
      txWrite(KEYS.clientes, clientes);
      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  // ── Movimientos / Historial ────────────────────────────────────────

  rest.get(
    /\/api\/fidelizacion\/movimientos/,
    (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const url = new URL(req.url);
      const clienteId = url.searchParams.get("clienteId");
      const premioId = url.searchParams.get("premioId");
      let movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
      if (clienteId) {
        movimientos = movimientos.filter((m: any) => m.clienteId === clienteId);
      }
      if (premioId) {
        movimientos = movimientos.filter((m: any) => m.premioId === premioId);
      }
      return res(
        ctx.status(200),
        ctx.json({ success: true, data: { movimientos } }),
      );
    },
  ),

  // ── Premios ────────────────────────────────────────────────────────

  rest.get(/\/api\/fidelizacion\/premios/, (_req: any, res: any, ctx: any) => {
    const premios = txRead(KEYS.premios, DEFAULT_PREMIOS);
    return res(ctx.status(200), ctx.json({ success: true, data: { premios } }));
  }),

  rest.post(
    /\/api\/fidelizacion\/premios/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const body = await req.json();
      const premios = txRead(KEYS.premios, DEFAULT_PREMIOS);
      const nuevo = { id: genId("premio"), ...body };
      premios.push(nuevo);
      txWrite(KEYS.premios, premios);
      return res(ctx.status(201), ctx.json({ success: true, data: nuevo }));
    },
  ),

  rest.put(
    /\/api\/fidelizacion\/premios\/(.+)/,
    async (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const id = req.params[0];
      const body = await req.json();
      const premios = txRead(KEYS.premios, DEFAULT_PREMIOS);
      const idx = premios.findIndex((p: any) => p.id === id);
      if (idx === -1) {
        return res(ctx.status(404), ctx.json({ success: false }));
      }
      runTransaction(() => {
        const stockAntes = premios[idx].stock ?? 0;
        Object.assign(premios[idx], body);
        const stockDespues = premios[idx].stock ?? 0;
        txWrite(KEYS.premios, premios);

        // Registrar movimiento de stock si cambió
        if (body.stock !== undefined && stockAntes !== stockDespues) {
          const movimientos = txRead(KEYS.movimientos, DEFAULT_MOVIMIENTOS);
          movimientos.unshift({
            id: genId("fid_m"),
            clienteId: "",
            clienteNombre: "Ajuste manual",
            tipo: "ajuste_stock_premio",
            puntos: 0,
            saldoResultante: 0,
            descripcion: `Stock ajustado de ${stockAntes} a ${stockDespues}`,
            premioId: id,
            premioNombre: premios[idx].nombre,
            stockAntes,
            stockDespues,
            usuarioId: "usr_1",
            userName: "Administrador",
            fecha: new Date().toISOString(),
          } as any);
          txWrite(KEYS.movimientos, movimientos);
        }
      });

      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  rest.delete(
    /\/api\/fidelizacion\/premios\/(.+)/,
    (req: any, res: any, ctx: any) => {
      if (isRealUser(req)) return;
      const id = req.params[0];
      let premios = txRead(KEYS.premios, DEFAULT_PREMIOS);
      premios = premios.filter((p: any) => p.id !== id);
      txWrite(KEYS.premios, premios);
      return res(ctx.status(200), ctx.json({ success: true }));
    },
  ),

  // ── Ranking ────────────────────────────────────────────────────────

  rest.get(/\/api\/fidelizacion\/ranking/, (_req: any, res: any, ctx: any) => {
    const clientes = txRead(KEYS.clientes, DEFAULT_CLIENTES);
    const sorted = [...clientes]
      .filter((c: any) => c.activo)
      .sort((a: any, b: any) => b.puntosActuales - a.puntosActuales);
    return res(
      ctx.status(200),
      ctx.json({ success: true, data: { clientes: sorted } }),
    );
  }),
];
